export type Story = {
  id: string;
  title: string;
  category: string;
  categoryName: string;
  summary: string;
  points: string[];
  tags: string[];
  cover: string;
  coverAlt: string;
  author: string;
  date: string;
  readTime: string;
  heat: number;
  source: string;
  body: string[];
};

export type Category = {
  key: string;
  name: string;
  desc: string;
  icon: string;
  count: number;
};

export const categories: Category[] = [
  { key: 'film', name: '影视动态', desc: '剧集、电影的最新动向与口碑变化', icon: 'ri-film-line', count: 42 },
  { key: 'show', name: '综艺看点', desc: '综艺节目的名场面与话题走向', icon: 'ri-tv-2-line', count: 28 },
  { key: 'music', name: '音乐现场', desc: '音乐节、演唱会与现场演出观察', icon: 'ri-music-2-line', count: 21 },
  { key: 'net', name: '网络热点', desc: '全网正在讨论的热门事件梳理', icon: 'ri-fire-line', count: 35 },
  { key: 'brand', name: '品牌风向', desc: '品牌话题、营销动作与消费趋势', icon: 'ri-shopping-bag-3-line', count: 19 },
  { key: 'star', name: '艺人观察', desc: '艺人口碑、作品与人气变化记录', icon: 'ri-user-star-line', count: 26 },
];

export const stories: Story[] = [
  {
    id: 'chang-ye-deng',
    title: '《长夜灯》口碑逆袭：一部慢热剧如何在一周内翻盘',
    category: 'film',
    categoryName: '影视动态',
    summary:
      '开播首周热度平平的古装剧《长夜灯》，因为第三集一场七分钟群像长镜头在社交平台二次发酵。相关话题连续四天停留在热榜前列，站内讨论量一周内上涨近三倍，成为近期少见的靠口碑翻盘的案例。',
    points: [
      '开播首日是同期新剧中讨论量最低的一部，评分一度停在及格线附近。',
      '第三集七分钟群像长镜头上线后，二创剪辑在多个平台集中扩散。',
      '主创在播出中期追加两场线上直播，带动大量路人回看前几集。',
      '站内讨论量较开播首周上涨约 280%，追更人群明显年轻化。',
    ],
    tags: ['古装剧', '口碑', '长镜头'],
    cover:
      'https://readdy.ai/api/search-image?query=Warm%20editorial%20magazine%20illustration%20of%20an%20empty%20traditional%20theater%20stage%20lit%20by%20a%20single%20paper%20lantern%2C%20cream%20background%20with%20terracotta%20and%20amber%20accents%2C%20flat%20geometric%20shapes%2C%20soft%20grain%20texture%2C%20minimal%20composition%2C%20high%20detail&width=800&height=600&seq=kanliao-cover-film-01&orientation=landscape',
    coverAlt: '戏剧化舞美与灯光构成的古装剧主题插画',
    author: '速览编辑部',
    date: '2026-09-10',
    readTime: '3 分钟',
    heat: 9820,
    source: '站内综合整理',
    body: [
      '慢热剧翻盘并不新鲜，但《长夜灯》这次的曲线格外陡。首播当天，它在同期上线的三部新剧中讨论量垫底，评分也长期徘徊在及格线附近，一度被认为是"叫好不叫座"的典型。',
      '转折点出现在第三集。一段七分钟的群像长镜头把三条人物线同时推到前台，运镜、调度和配乐的完成度超出同类型作品的平均水准。当晚就有剪辑账号把这段单独截出，随后在多个平台形成扩散。',
      '真正让热度沉淀下来的，是主创团队在播出中期的两场线上直播。他们没有重复剧情，而是聊了这场长镜头的调度思路和演员走位，把"技术流"的讨论引向了内容本身。',
      '从数据上看，回看率的变化最值得注意。多家平台的追更数据显示，新加入的观众里有相当一部分是从第三集倒回去补看前两集的，这说明口碑传播确实带来了净增用户，而不是内部流量的来回搬运。',
    ],
  },
  {
    id: 'summer-variety-war',
    title: '暑期综艺混战：慢综艺回温，竞技类节目为何集体承压',
    category: 'show',
    categoryName: '综艺看点',
    summary:
      '今年暑期档综艺数量创下近年新高，但热度分布高度集中。慢节奏生活类节目整体回温，而依赖强对抗的竞技综艺表现分化明显，多档节目在播出过半后热度回落。',
    points: [
      '暑期上线的综艺数量较去年同期增加约两成。',
      '生活观察类节目平均热度回升，观众停留时长更稳定。',
      '竞技类综艺热度分化，仅两档节目维持住首播水平。',
      '短视频二创成为综艺出圈的主要路径。',
    ],
    tags: ['综艺', '暑期档', '慢综艺'],
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20studio%20variety%20show%20set%20with%20colorful%20soft%20spotlights%20and%20rounded%20geometric%20props%2C%20warm%20cream%20backdrop%2C%20terracotta%20amber%20and%20teal%20accents%2C%20flat%20illustration%20style%2C%20editorial%20magazine%20aesthetic%2C%20subtle%20grain%20texture&width=800&height=600&seq=kanliao-cover-show-02&orientation=landscape',
    coverAlt: '综艺演播厅灯光与道具构成的抽象插画',
    author: '快看观察员',
    date: '2026-09-09',
    readTime: '4 分钟',
    heat: 8640,
    source: '站内综合整理',
    body: [
      '这个夏天，观众打开视频平台能看到的综艺比往年更多，但真正被讨论的却更少。数量上去了，话题却更加集中在少数几档节目身上。',
      '慢综艺的回温是最明显的变化。生活观察、旅行体验类节目虽然没有爆发式的首播数据，但观众停留时长和完播率都更稳定，长尾效应明显。',
      '相比之下，竞技类综艺承压更重。强对抗设定确实能在首播时制造话题，但一旦赛制被观众摸清，新鲜感下降得很快，多档节目在播出过半后热度回落。',
      '值得注意的是，二创已经成为综艺出圈的主路径。名场面、金句和嘉宾互动被剪成短视频后，反过来为节目导流，这也让节目组在剪辑节奏上更在意"可被二创的瞬间"。',
    ],
  },
  {
    id: 'music-festival-economy',
    title: '音乐节走出大城市：下沉市场正在重塑现场演出',
    category: 'music',
    categoryName: '音乐现场',
    summary:
      '今年多个音乐节把举办地选在二三线城市甚至县域，票务数据显示这些场次的上座率和复购意愿并不逊色。交通、住宿与本地消费的联动，让音乐节成为地方文旅的新抓手。',
    points: [
      '今年下半年计划举办的大型音乐节中，过半场次落地非一线城市。',
      '下沉场次的平均上座率与一线场地基本持平。',
      '跨城观演比例上升，带动周边住宿与餐饮消费。',
      '本地乐队与新人获得了更多登台机会。',
    ],
    tags: ['音乐节', '现场演出', '文旅'],
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20music%20festival%20scene%20at%20sunset%20with%20flowing%20sound%20wave%20shapes%20and%20silhouetted%20crowd%2C%20warm%20amber%20and%20terracotta%20gradient%20sky%2C%20cream%20tones%2C%20flat%20editorial%20illustration%2C%20minimal%20geometric%20composition%2C%20soft%20grain&width=800&height=600&seq=kanliao-cover-music-03&orientation=landscape',
    coverAlt: '黄昏音乐节声波与人群剪影抽象插画',
    author: '料小满',
    date: '2026-09-08',
    readTime: '3 分钟',
    heat: 7210,
    source: '站内综合整理',
    body: [
      '过去音乐节几乎是北上广深的固定项目，今年这个格局被明显打破。多个知名音乐节品牌把举办地挪到了二三线城市，甚至直接落进县域。',
      '从票务数据看，这种下沉并不是妥协。非一线城市的场次上座率与一线场地基本持平，部分场次因为场地更宽敞，反而获得了更好的现场体验评价。',
      '更关键的是跨城观演的比例上升。观众愿意为一个音乐节安排一次短途旅行，交通、住宿和周边消费随之被带动，音乐节逐渐从单纯的演出变成地方文旅的抓手。',
      '对新人和本地乐队来说，这也是难得的机会。大量的分舞台和暖场时段需要内容填充，过去很难登上大舞台的乐队因此获得了曝光窗口。',
    ],
  },
  {
    id: 'short-drama-overseas',
    title: '短剧出海进入下半场：从买量驱动转向内容竞争',
    category: 'net',
    categoryName: '网络热点',
    summary:
      '短剧出海在经历了一年多的爆发式增长后，进入更强调内容质量的阶段。单靠买量拉新的模式边际效益下降，制作精良、题材更本地化的作品开始占据榜单前列。',
    points: [
      '短剧出海的头部应用月活仍在增长，但增速明显放缓。',
      '买量成本上升，单靠投放拉新的模式难以为继。',
      '本地化题材与本土演员的作品在榜单中占比提升。',
      '精品短剧的单集制作成本普遍高于去年同期。',
    ],
    tags: ['短剧', '出海', '内容产业'],
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20vertical%20phone%20screens%20floating%20in%20warm%20cream%20space%20with%20terracotta%20and%20amber%20gradient%20blocks%2C%20minimal%20geometric%20editorial%20illustration%2C%20soft%20shadows%20and%20grain%20texture%2C%20clean%20modern%20composition&width=800&height=600&seq=kanliao-cover-net-04&orientation=landscape',
    coverAlt: '悬浮竖屏与色块构成的短剧主题抽象插画',
    author: '瓜田记录员',
    date: '2026-09-07',
    readTime: '4 分钟',
    heat: 6890,
    source: '站内综合整理',
    body: [
      '短剧出海已经不是新鲜事，但今年的竞争逻辑正在改变。过去靠密集投放快速拉新的打法，边际效益开始下降，买量成本的抬升让不少团队重新思考投入结构。',
      '一个直观的信号是榜单结构。早期占据前列的多是高强度反转、节奏极快的作品，如今本地化题材、启用本土演员的剧集开始稳定进入榜单，说明观众对内容的接受度在提高。',
      '制作端的变化同样明显。精品短剧的单集成本普遍高于去年同期，拍摄周期更长，对剧本的打磨也更细致，这和"一周拍完"的早期印象已经相去甚远。',
      '对从业者来说，下半场的门槛在于内容判断力。能不能在几十秒内建立冲突，又能不能让观众愿意为下一集付费，考验的其实是故事本身。',
    ],
  },
  {
    id: 'guohuo-crossover',
    title: '国货彩妆联名降温：消费者开始为产品本身买单',
    category: 'brand',
    categoryName: '品牌风向',
    summary:
      '曾经靠高频联名快速出圈的国货彩妆，今年明显放慢了联名节奏。多家品牌把资源转回产品研发与经典款迭代，消费者对联名的态度也从抢购转向理性挑选。',
    points: [
      '头部国货彩妆品牌今年的联名数量较去年明显下降。',
      '经典单品的迭代版本贡献了主要销量增长。',
      '消费者更关注成分、色号和肤感，而非包装话题。',
      '联名款的生命周期缩短，二次复购依赖产品口碑。',
    ],
    tags: ['国货', '彩妆', '消费趋势'],
    cover:
      'https://readdy.ai/api/search-image?query=Minimal%20editorial%20still%20life%20of%20abstract%20cosmetic%20bottles%20and%20round%20compacts%20on%20warm%20cream%20surface%2C%20terracotta%20and%20amber%20accent%20shapes%2C%20soft%20natural%20light%2C%20flat%20illustration%20style%2C%20clean%20geometric%20composition&width=800&height=600&seq=kanliao-cover-brand-05&orientation=landscape',
    coverAlt: '暖色背景下抽象美妆瓶罐静物插画',
    author: '速览编辑部',
    date: '2026-09-06',
    readTime: '3 分钟',
    heat: 6120,
    source: '站内综合整理',
    body: [
      '联名曾是国货彩妆最有效的增长杠杆，一款成功的联名可以在几天内卖断货。但今年的情况不太一样，头部品牌的联名频率明显下降。',
      '把资源收回产品本身，是这些品牌共同的选择。经典单品的迭代版本贡献了主要的销量增长，色号、质地和肤感的微调，比换一个包装更能打动复购用户。',
      '消费者的态度也变了。早期联名带来的是"抢到就是赚到"的稀缺感，如今大家更愿意先看成分表和使用反馈，再决定要不要为话题买单。',
      '这不意味着联名失效，而是联名的门槛提高了。只有当产品本身足够扎实，联名才会成为放大器，否则只是一次性的流量采购。',
    ],
  },
  {
    id: 'new-actors-rise',
    title: '新人演员集中出圈：剧集市场正在重新分配机会',
    category: 'star',
    categoryName: '艺人观察',
    summary:
      '今年多部剧集选择启用新人担纲主角，其中数位演员凭角色完成度获得关注。制作方对性价比和新鲜感的权衡，正在改变过去由少数熟脸垄断主角的局面。',
    points: [
      '今年上线的剧集中，新人担纲主角的比例高于往年。',
      '观众对角色的讨论度明显超过对演员本人资历的关注。',
      '新人片酬结构更灵活，降低了剧集的成本压力。',
      '配角出彩成为新人获得关注的重要路径。',
    ],
    tags: ['新人演员', '选角', '剧集'],
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20editorial%20portrait%20silhouette%20of%20a%20figure%20under%20a%20warm%20studio%20spotlight%2C%20cream%20background%20with%20terracotta%20and%20amber%20gradient%20shapes%2C%20minimal%20geometric%20illustration%2C%20soft%20grain%20texture%2C%20elegant%20magazine%20aesthetic&width=800&height=600&seq=kanliao-cover-star-06&orientation=landscape',
    coverAlt: '暖色聚光灯下人物剪影抽象插画',
    author: '快看观察员',
    date: '2026-09-05',
    readTime: '3 分钟',
    heat: 5980,
    source: '站内综合整理',
    body: [
      '过去主角往往是少数几位熟脸的轮转，今年这个格局出现了松动。多部剧集把主角位置交给了此前作品不多的新人，其中几位凭角色完成度获得了持续关注。',
      '制作方的算盘并不复杂。新人片酬结构更灵活，能把更多预算留给美术、服化道和后制，这对一部剧的整体质感影响很大。',
      '更重要的是观众的注意力在转移。讨论区的焦点更多落在角色是否立得住，而不是演员过去演过什么，这给新人提供了相对公平的起跑线。',
      '另一条路径是配角出彩。不少新人是从一个有记忆点的配角被记住，再逐步拿到更重的戏份，这种积累方式虽然慢，但更稳。',
    ],
  },
  {
    id: 'old-drama-revival',
    title: '老剧翻红成常态：经典重播为何比新剧更耐看',
    category: 'net',
    categoryName: '网络热点',
    summary:
      '平台片单里，一批播出超过五年的老剧长期占据观看时长前列。它们没有宣发预算，却凭稳定的口碑持续吸引新观众，成为内容库中最"抗跌"的资产。',
    points: [
      '多部播出五年以上的老剧进入平台长期观看时长前列。',
      '老剧几乎没有宣发投入，靠口碑自然增长。',
      '年轻观众占比上升，弹幕和短评带来二次传播。',
      '平台开始把老剧修复与专题策划作为常规运营手段。',
    ],
    tags: ['老剧', '重播', '口碑'],
    cover:
      'https://readdy.ai/api/search-image?query=Retro%20abstract%20television%20set%20with%20warm%20scan%20lines%20and%20rounded%20screen%2C%20cream%20and%20amber%20palette%20with%20terracotta%20accents%2C%20flat%20editorial%20illustration%2C%20minimal%20geometric%20composition%2C%20soft%20grain%20texture&width=800&height=600&seq=kanliao-cover-net-07&orientation=landscape',
    coverAlt: '复古电视机与暖色扫描线抽象插画',
    author: '料小满',
    date: '2026-09-04',
    readTime: '3 分钟',
    heat: 5670,
    source: '站内综合整理',
    body: [
      '在平台的观看时长榜上，一批播出超过五年的老剧长期停留。它们没有首播宣发，也没有话题营销，却比许多新剧更"抗跌"。',
      '原因其实不难理解。老剧的口碑已经被时间验证，观众选择它的心理成本极低，不需要承受"追了几集发现不好看"的风险。',
      '有趣的是，新观众并不少。年轻观众用弹幕和短评把老剧重新激活，一些经典台词和片段被剪成短视频，形成了新的传播循环。',
      '平台也开始顺势而为，把老剧的画面修复和专题策划变成常规运营手段，把这份稳定的流量纳入长期内容库管理。',
    ],
  },
  {
    id: 'suspense-cluster',
    title: '悬疑剧扎堆上线：同质化之后，观众在等什么',
    category: 'film',
    categoryName: '影视动态',
    summary:
      '近两个月上线的新剧中，悬疑题材占比明显偏高。高密度的同类供给让观众的口味快速提高，单纯的氛围营造和反转堆叠已经很难带来惊喜。',
    points: [
      '悬疑题材在近两个月新剧中占比明显提升。',
      '观众对"为反转而反转"的套路容忍度下降。',
      '人物动机是否成立成为差评集中出现的焦点。',
      '具备社会议题落点的作品评分更为稳定。',
    ],
    tags: ['悬疑剧', '同质化', '口碑'],
    cover:
      'https://readdy.ai/api/search-image?query=Atmospheric%20abstract%20noir%20scene%20with%20layered%20fog%20and%20geometric%20doorway%20shapes%2C%20warm%20terracotta%20and%20amber%20light%20against%20deep%20cream%20tones%2C%20flat%20editorial%20illustration%2C%20minimal%20composition%2C%20soft%20grain&width=800&height=600&seq=kanliao-cover-film-08&orientation=landscape',
    coverAlt: '雾气与门框构成的悬疑氛围抽象插画',
    author: '速览编辑部',
    date: '2026-09-03',
    readTime: '4 分钟',
    heat: 5240,
    source: '站内综合整理',
    body: [
      '悬疑题材一直是平台的"安全牌"，但今年的密集程度还是超出了许多人的预期。近两个月上线的新剧中，悬疑占比明显偏高。',
      '供给一多，差异就被放大了。观众很快发现，很多作品共用同一套模板：一个离奇命案、几条互相牵扯的线索、最后十分钟的强行反转。',
      '差评最集中的地方是人物动机。当角色行为只服务于反转，而不是出于可信的动机时，所谓的烧脑就变成了故弄玄虚。',
      '相形之下，评分更稳的作品往往有一个社会议题的落点。它们把悬疑作为外壳，真正想讨论的是某种现实处境，这让案件之外的部分也值得看。',
    ],
  },
  {
    id: 'virtual-idol',
    title: '虚拟偶像走进线下：一场演出背后的技术与生意',
    category: 'music',
    categoryName: '音乐现场',
    summary:
      '虚拟偶像不再只存在于直播间，今年多场线下演出把全息投影搬进了真实场馆。技术成本下降与粉丝消费意愿提升，让这条赛道的商业化路径逐渐清晰。',
    points: [
      '今年线下虚拟演出场次明显增加，场馆规模也在扩大。',
      '全息投影与实时动捕的成本较两年前有所下降。',
      '周边与限定票务成为主要收入来源。',
      '粉丝对"虚拟"的接受度比外界预期更高。',
    ],
    tags: ['虚拟偶像', '全息', '演出'],
    cover:
      'https://readdy.ai/api/search-image?query=Futuristic%20abstract%20holographic%20figure%20on%20a%20concert%20stage%2C%20warm%20amber%20and%20rose%20light%20bands%20against%20cream%20background%2C%20flat%20editorial%20illustration%2C%20minimal%20geometric%20shapes%2C%20soft%20grain%20texture&width=800&height=600&seq=kanliao-cover-music-09&orientation=landscape',
    coverAlt: '全息投影人物与光带构成的虚拟演出现场插画',
    author: '瓜田记录员',
    date: '2026-09-02',
    readTime: '3 分钟',
    heat: 4780,
    source: '站内综合整理',
    body: [
      '虚拟偶像早期只活跃在直播和短视频里，今年它们开始频繁出现在线下场馆。从几百人的小型演出到数千人的剧场，场次和规模都在上升。',
      '背后是技术成本的下降。全息投影和实时动捕的方案逐渐成熟，过去需要定制硬件才能完成的呈现，如今有更标准化的解决方案可用。',
      '商业化路径也随之清晰。限定票务、周边衍生和线上直播构成主要收入结构，其中周边往往贡献了最可观的利润。',
      '一个容易被忽略的事实是，粉丝对"虚拟"的接受度比外界想象得高。他们讨论的是演出完成度和互动设计，而不是表演者是否真实存在，这本身就是这个品类走向成熟的信号。',
    ],
  },
  {
    id: 'livestream-shift',
    title: '直播带货转向内容化：叫卖式话术正在退场',
    category: 'brand',
    categoryName: '品牌风向',
    summary:
      '直播间的叫卖式话术今年明显减少，越来越多的主播开始用专业讲解、场景演示和长时段陪伴来留住观众。转化逻辑从"冲动下单"转向"信任复购"。',
    points: [
      '头部直播间平均讲解时长上升，叫卖式话术占比下降。',
      '专业垂类主播的复购率高于综合类直播间。',
      '场景化演示成为提升客单价的有效手段。',
      '品牌自播比例继续上升，内容预算随之增加。',
    ],
    tags: ['直播带货', '内容化', '复购'],
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20live%20streaming%20studio%20scene%20with%20ring%20light%20and%20rounded%20screen%20frames%2C%20warm%20cream%20backdrop%20with%20terracotta%20and%20amber%20accent%20shapes%2C%20flat%20editorial%20illustration%2C%20minimal%20geometric%20composition%2C%20soft%20grain&width=800&height=600&seq=kanliao-cover-brand-10&orientation=landscape',
    coverAlt: '环形补光灯与屏幕轮廓构成的直播间抽象插画',
    author: '料小满',
    date: '2026-09-01',
    readTime: '3 分钟',
    heat: 4320,
    source: '站内综合整理',
    body: [
      '如果你最近看过几场直播，会发现一个明显的变化：那种连续喊口号、催着下单的直播间少了，取而代之的是更长的讲解和更慢的节奏。',
      '这不是主播变温柔了，而是转化逻辑在变。冲动下单的退货率一直居高不下，平台和品牌都开始把注意力转向复购和客单价。',
      '专业垂类主播因此更占优势。他们能在几十秒里说清楚一个参数的差异，这种说服力是话术替代不了的，复购率也明显更高。',
      '品牌自播的比例继续上升，内容预算随之增加。直播间正在从临时的清货渠道，变成品牌长期经营用户的阵地。',
    ],
  },
];